import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import { Sidebar } from '../components/layout/Sidebar';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const [panicMode, setPanicMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isSubUser } = useAuth();

  // Sub-users belong in the trading portal, not here
  useEffect(() => {
    if (isSubUser) {
      navigate('/trade', { replace: true });
    }
  }, [isSubUser, navigate]);

  const checkPanicStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/panic/status`, { withCredentials: true });
      setPanicMode(data.panic_stopped);
    } catch (error) {
      console.error('Failed to check panic status:', error);
    }
  }, []);

  useEffect(() => {
    checkPanicStatus();
  }, [checkPanicStatus]);

  const handlePanicToggle = () => setPanicMode(prev => !prev);
  const isOverview = location.pathname === '/dashboard';

  return (
    <div className="min-h-screen bg-[hsl(220,20%,8%)]">
      <Header onPanicStop={handlePanicToggle} panicMode={panicMode} />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-6 overflow-y-auto">
          {panicMode && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3" data-testid="panic-banner">
              <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
              <div>
                <p className="font-medium text-red-400">PANIC MODE ACTIVE</p>
                <p className="text-sm text-red-300/80">All bots stopped. Click "RESET PANIC" to resume operations.</p>
              </div>
            </div>
          )}
          {isOverview ? <OverviewContent panicMode={panicMode} /> : <Outlet context={{ panicMode }} />}
        </main>
      </div>
    </div>
  );
}

function OverviewContent({ panicMode }) {
  const [analytics, setAnalytics] = useState(null);
  const [volumeBots, setVolumeBots] = useState([]);
  const [lureBots, setLureBots] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, volumeRes, lureRes] = await Promise.all([
          axios.get(`${API_URL}/api/analytics/market`, { withCredentials: true }),
          axios.get(`${API_URL}/api/bot/volume/status`, { withCredentials: true }),
          axios.get(`${API_URL}/api/bot/lure/status`, { withCredentials: true })
        ]);
        setAnalytics(analyticsRes.data);
        setVolumeBots(volumeRes.data.bots);
        setLureBots(lureRes.data.bots);
      } catch (error) {
        console.error('Failed to fetch overview data:', error);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const activeVolumeBots = volumeBots.filter(b => b.status === 'running').length;
  const activeLureBots = lureBots.filter(b => b.status === 'running').length;

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <div>
        <h1 className="text-2xl font-bold font-['Outfit'] text-white">Dashboard Overview</h1>
        <p className="text-[hsl(215,16%,56%)]">Real-time system status and market metrics</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="ticker-card" data-testid="ticker-price">
          <div className="w-10 h-10 rounded-lg bg-[#E8520F]/10 flex items-center justify-center">
            <span className="text-[#E8520F] font-bold">$</span>
          </div>
          <div>
            <p className="ticker-label">Price</p>
            <p className="ticker-value">${analytics?.price?.toFixed(6) || '0.000000'}</p>
            <p className={`text-xs ${analytics?.price_change_24h >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
              {analytics?.price_change_24h >= 0 ? '+' : ''}{analytics?.price_change_24h?.toFixed(2) || '0.00'}%
            </p>
          </div>
        </div>
        <div className="ticker-card" data-testid="ticker-liquidity">
          <div className="w-10 h-10 rounded-lg bg-[#00CED1]/10 flex items-center justify-center">
            <span className="text-[#00CED1] font-bold">L</span>
          </div>
          <div>
            <p className="ticker-label">Liquidity</p>
            <p className="ticker-value">${analytics?.liquidity?.toLocaleString() || '0'}</p>
          </div>
        </div>
        <div className="ticker-card" data-testid="ticker-volume">
          <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
            <span className="text-[#10B981] font-bold">V</span>
          </div>
          <div>
            <p className="ticker-label">24h Volume</p>
            <p className="ticker-value">${analytics?.volume_24h?.toLocaleString() || '0'}</p>
          </div>
        </div>
        <div className="ticker-card" data-testid="ticker-bot-volume">
          <div className="w-10 h-10 rounded-lg bg-[#D4A017]/10 flex items-center justify-center">
            <span className="text-[#D4A017] font-bold">B</span>
          </div>
          <div>
            <p className="ticker-label">Bot Volume</p>
            <p className="ticker-value">${analytics?.bot_volume?.toLocaleString() || '0'}</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="solar-card p-6" data-testid="volume-bots-status">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold font-['Outfit'] text-white">Volume Bots</h2>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${panicMode ? 'bg-red-500/15 text-red-400' : activeVolumeBots > 0 ? 'bg-green-500/15 text-green-400' : 'bg-[hsl(220,16%,18%)] text-[hsl(215,16%,50%)]'}`}>
              {panicMode ? 'STOPPED' : activeVolumeBots > 0 ? `${activeVolumeBots} Active` : 'Inactive'}
            </div>
          </div>
          {volumeBots.length > 0 ? (
            <div className="space-y-2">
              {volumeBots.slice(0, 3).map(bot => (
                <div key={bot.id} className="flex items-center justify-between p-3 bg-[hsl(220,16%,14%)] rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${bot.status === 'running' ? 'bg-[#10B981]' : bot.status === 'panic_stopped' ? 'bg-[#EF4444]' : 'bg-[hsl(215,16%,40%)]'}`} />
                    <span className="text-sm font-mono text-[hsl(215,16%,56%)]">Bot #{bot.id}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-white">{bot.trades_executed} trades</p>
                    <p className="text-xs text-[hsl(215,16%,56%)]">${bot.total_volume?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[hsl(215,16%,50%)] text-center py-4">No volume bots configured</p>
          )}
        </div>
        <div className="solar-card p-6" data-testid="lure-bots-status">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold font-['Outfit'] text-white">Lure/Sniper Bots</h2>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${panicMode ? 'bg-red-500/15 text-red-400' : activeLureBots > 0 ? 'bg-green-500/15 text-green-400' : 'bg-[hsl(220,16%,18%)] text-[hsl(215,16%,50%)]'}`}>
              {panicMode ? 'STOPPED' : activeLureBots > 0 ? `${activeLureBots} Active` : 'Inactive'}
            </div>
          </div>
          {lureBots.length > 0 ? (
            <div className="space-y-2">
              {lureBots.slice(0, 3).map(bot => (
                <div key={bot.id} className="flex items-center justify-between p-3 bg-[hsl(220,16%,14%)] rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${bot.status === 'running' ? 'bg-[#10B981]' : bot.status === 'panic_stopped' ? 'bg-[#EF4444]' : 'bg-[hsl(215,16%,40%)]'}`} />
                    <span className="text-sm font-mono text-[hsl(215,16%,56%)]">Bot #{bot.id}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-white">{bot.micro_buys_executed} micro-buys</p>
                    <p className="text-xs text-[hsl(215,16%,56%)]">{bot.trackers_triggered?.length || 0} trackers hit</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[hsl(215,16%,50%)] text-center py-4">No lure bots configured</p>
          )}
        </div>
      </div>
      <div className="solar-card p-6">
        <h2 className="text-lg font-semibold font-['Outfit'] text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a href="/dashboard/volume-bot" className="p-4 bg-[#E8520F]/5 rounded-lg border border-[#E8520F]/20 hover:bg-[#E8520F]/10 transition-colors">
            <p className="font-medium text-[#E8520F]">Start Volume Bot</p>
            <p className="text-xs text-[hsl(215,16%,50%)] mt-1">Configure uptime trading</p>
          </a>
          <a href="/dashboard/defi-exchanges" className="p-4 bg-[#00CED1]/5 rounded-lg border border-[#00CED1]/20 hover:bg-[#00CED1]/10 transition-colors">
            <p className="font-medium text-[#00CED1]">DeFi Exchanges</p>
            <p className="text-xs text-[hsl(215,16%,50%)] mt-1">Select tokens from DEXs</p>
          </a>
          <a href="/dashboard/zk-vault" className="p-4 bg-[#10B981]/5 rounded-lg border border-[#10B981]/20 hover:bg-[#10B981]/10 transition-colors">
            <p className="font-medium text-[#10B981]">ZK Deposit</p>
            <p className="text-xs text-[hsl(215,16%,50%)] mt-1">Privacy mixer</p>
          </a>
          <a href="/dashboard/analytics" className="p-4 bg-[#D4A017]/5 rounded-lg border border-[#D4A017]/20 hover:bg-[#D4A017]/10 transition-colors">
            <p className="font-medium text-[#D4A017]">View Analytics</p>
            <p className="text-xs text-[hsl(215,16%,50%)] mt-1">Market charts</p>
          </a>
        </div>
      </div>
    </div>
  );
}
